import { publicHttpUrl, publicSiteUrl } from '../publicUrl';

type RestaurantSchemaData = {
  slug: string;
  status?: string | null;
  offersFirstTable?: boolean | null;
  menuTitle?: string | null;
  street?: string | null;
  city?: string | null;
  zip?: string | null;
  lat?: string | number | null;
  lng?: string | number | null;
  phone?: string | null;
  website?: string | null;
  menuLink?: string | null;
  mainsPriceRange?: string | null;
  rating?: number | null;
  reviewsCount?: number | null;
  cuisines?: {
    edges?: ({ node?: { title?: string | null; category?: string | null } | null } | null)[] | null;
  } | null;
};

type SiteSchemaData = {
  websiteURL?: string | null;
  countryCode?: string | null;
  language?: string | null;
};

const coordinate = (value: string | number | null | undefined, limit: number) => {
  if (value == null || (typeof value === 'string' && !value.trim())) return undefined;
  const number = Number(value);
  return Number.isFinite(number) && Math.abs(number) <= limit ? number : undefined;
};

export const buildRestaurantSchema = (
  restaurant: RestaurantSchemaData,
  site: SiteSchemaData,
  images: string[]
) => {
  const pageUrl = publicHttpUrl(restaurant.slug, publicSiteUrl(site.websiteURL));
  const latitude = coordinate(restaurant.lat, 90);
  const longitude = coordinate(restaurant.lng, 180);
  const cuisines = Array.from(
    new Set(
      (restaurant.cuisines?.edges || [])
        .filter((edge) => edge?.node?.category === 'Cuisine')
        .map((edge) => edge.node.title?.trim())
        .filter(Boolean)
    )
  );
  const menuUrl = publicHttpUrl(restaurant.menuLink, pageUrl);
  const countryCode = site.countryCode?.trim().toUpperCase();
  // SiteConfig stores the UK as "UK"; schema.org addressCountry wants the ISO code.
  const addressCountry = countryCode === 'UK' ? 'GB' : countryCode;
  const canBookFirstTable = restaurant.status === 'Live' && restaurant.offersFirstTable === true;
  const { rating, reviewsCount } = restaurant;
  const hasRating =
    Number.isFinite(rating) &&
    rating >= 1 &&
    rating <= 5 &&
    Number.isInteger(reviewsCount) &&
    reviewsCount > 0;

  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    '@id': pageUrl ? `${pageUrl}#restaurant` : undefined,
    name: restaurant.menuTitle,
    url: pageUrl,
    sameAs: publicHttpUrl(restaurant.website),
    image: images.length ? images : undefined,
    address: {
      '@type': 'PostalAddress',
      streetAddress: restaurant.street || undefined,
      addressLocality: restaurant.city || undefined,
      postalCode: restaurant.zip || undefined,
      addressCountry: /^[A-Z]{2}$/.test(addressCountry || '') ? addressCountry : undefined,
    },
    geo:
      latitude !== undefined && longitude !== undefined
        ? {
            '@type': 'GeoCoordinates',
            latitude,
            longitude,
          }
        : undefined,
    telephone: restaurant.phone || undefined,
    menu: menuUrl,
    hasMenu: menuUrl,
    servesCuisine: cuisines.length ? cuisines : undefined,
    priceRange: restaurant.mainsPriceRange ? `Mains: ${restaurant.mainsPriceRange}` : undefined,
    // Match the five-star rating and count displayed by RestaurantTitle.
    aggregateRating: hasRating
      ? {
          '@type': 'AggregateRating',
          ratingValue: rating,
          worstRating: 1,
          bestRating: 5,
          reviewCount: reviewsCount,
        }
      : undefined,
    potentialAction:
      pageUrl && canBookFirstTable
        ? {
            '@type': 'ReserveAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: pageUrl,
              inLanguage: site.language || undefined,
              actionPlatform: [
                'https://schema.org/DesktopWebPlatform',
                'https://schema.org/IOSPlatform',
                'https://schema.org/AndroidPlatform',
              ],
            },
            result: { '@type': 'FoodEstablishmentReservation', name: 'Book a First Table' },
          }
        : undefined,
  };
};
